import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, BaseModelObject, ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumDocumentFileType } from "./enums/enumDocumentFileType.generated";
import { EnumDocumentTagType } from "./enums/enumDocumentTagType.generated";
import { EnumDocumentType } from "./enums/enumDocumentType.generated";

// Model Objects

export class DataTag extends ModelObject<DataTag> {

    public tagKey: string = "";
    public tagType: EnumDocumentTagType = EnumDocumentTagType.string;
    private _stringVal: string | null = null;
    public get stringVal(): string | null { return this._stringVal }
    public set stringVal(value: string | null) {
      this._stringVal = value === undefined ? null : value;
    }
    private _integerVal: number | null = null;
    public get integerVal(): number | null { return this._integerVal }
    public set integerVal(value: number | null) {
      this._integerVal = value === undefined ? null : value;
    }
    private _longVal: number | null = null;
    public get longVal(): number | null { return this._longVal }
    public set longVal(value: number | null) {
      this._longVal = value === undefined ? null : value;
    }
    private _doubleVal: number | null = null;
    public get doubleVal(): number | null { return this._doubleVal }
    public set doubleVal(value: number | null) {
      this._doubleVal = value === undefined ? null : value;
    }
    private _booleanVal: boolean | null = null;
    public get booleanVal(): boolean | null { return this._booleanVal }
    public set booleanVal(value: boolean | null) {
      this._booleanVal = value === undefined ? null : value;
    }
    private _dateVal: LocalDate | null = null;
    public get dateVal(): LocalDate | null { return this._dateVal }
    public set dateVal(value: LocalDate | null) {
      this._dateVal = value === undefined ? null : value;
    }
    private _timeVal: LocalTime | null = null;
    public get timeVal(): LocalTime | null { return this._timeVal }
    public set timeVal(value: LocalTime | null) {
      this._timeVal = value === undefined ? null : value;
    }
    private _timestampVal: Instant | null = null;
    public get timestampVal(): Instant | null { return this._timestampVal }
    public set timestampVal(value: Instant | null) {
      this._timestampVal = value === undefined ? null : value;
    }
    private _imageVal: Uint8Array | null = null;
    public get imageVal(): Uint8Array | null { return this._imageVal }
    public set imageVal(value: Uint8Array | null) {
      this._imageVal = value === undefined ? null : value;
    }
    public arrayVal: DataTagSet[] = [];
    private _objectVal: DataTagSet | null = null;
    public get objectVal(): DataTagSet | null { return this._objectVal }
    public set objectVal(value: DataTagSet | null) {
      this._objectVal = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.tagKey = jsonSerializedObject.tagKey;
          this.tagType = EnumDocumentTagType.getByValue(jsonSerializedObject.tagType, this.tagType);
          this.stringVal = jsonSerializedObject.stringVal == null ? null : jsonSerializedObject.stringVal;
          this.integerVal = jsonSerializedObject.integerVal == null ? null : jsonSerializedObject.integerVal;
          this.longVal = jsonSerializedObject.longVal == null ? null : jsonSerializedObject.longVal;
          this.doubleVal = jsonSerializedObject.doubleVal == null ? null : jsonSerializedObject.doubleVal;
          this.booleanVal = jsonSerializedObject.booleanVal == null ? null : jsonSerializedObject.booleanVal;
          this.dateVal = jsonSerializedObject.dateVal == null ? null : LocalDate.parse(jsonSerializedObject.dateVal);
          this.timeVal = jsonSerializedObject.timeVal == null ? null : LocalTime.parse(jsonSerializedObject.timeVal);
          this.timestampVal = jsonSerializedObject.timestampVal == null ? null : Instant.parse(jsonSerializedObject.timestampVal);
          this.imageVal = converters.base64ToUint8Array(jsonSerializedObject.imageVal);
          this.arrayVal = jsonSerializedObject.arrayVal.map((v: any) => new DataTagSet(v));
          this.objectVal = jsonSerializedObject.objectVal == null ? null : new DataTagSet(jsonSerializedObject.objectVal);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.tagKey = this.tagKey;
        toRet.tagType = this.tagType.value;
        toRet.stringVal = this.stringVal == null ? null : this.stringVal;
        toRet.integerVal = this.integerVal == null ? null : this.integerVal;
        toRet.longVal = this.longVal == null ? null : this.longVal;
        toRet.doubleVal = this.doubleVal == null ? null : this.doubleVal;
        toRet.booleanVal = this.booleanVal == null ? null : this.booleanVal;
        toRet.dateVal = this.dateVal == null ? null : this.dateVal.toString();
        toRet.timeVal = this.timeVal == null ? null : this.timeVal.toString();
        toRet.timestampVal = this.timestampVal == null ? null : this.timestampVal.toString();
        toRet.imageVal = converters.uint8ArrayToBase64(this.imageVal);
        toRet.arrayVal = this.arrayVal.map(v => v.toJsonSerializedObject());
        toRet.objectVal = this.objectVal == null ? null : this.objectVal?.toJsonSerializedObject() ?? null;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "integerVal", rule: (v: any) => v == null || v >= -2147483648 || "IntegerVal is not greater than minimum allowed value: -2147483648"},
            {field: "integerVal", rule: (v: any) => v == null || v <= 2147483647 || "IntegerVal is above maximum allowed value: 2147483647"},
            {field: "longVal", rule: (v: any) => v == null || v >= -9223372036854775808 || "LongVal is not greater than minimum allowed value: -9223372036854775808"},
            {field: "longVal", rule: (v: any) => v == null || v <= 9223372036854775807 || "LongVal is above maximum allowed value: 9223372036854775807"},
            {field: "arrayVal", rule: (v: any) => !!v || "arrayVal is required"},
            {field: "arrayVal", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as DataTagSet[]).reduce((acc: Array<string>, curr: DataTagSet) => acc.concat(curr.validate()), new Array<string>());
            }},
            {field: "objectVal", rule: (v: any) => (v === null) || v.validate()},
    ];
}


export class DataTagSet extends ModelObject<DataTagSet> {

    public tags: DataTag[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.tags = jsonSerializedObject.tags.map((v: any) => new DataTag(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.tags = this.tags.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "tags", rule: (v: any) => !!v || "tags is required"},
            {field: "tags", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as DataTag[]).reduce((acc: Array<string>, curr: DataTag) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class DocumentPart extends ModelObject<DocumentPart> {

    private _documentTemplateId: Guid | null = null;
    /**
    * Optional document template, otherwise it will use default
    */
    public get documentTemplateId(): Guid | null { return this._documentTemplateId }
    public set documentTemplateId(value: Guid | null) {
      this._documentTemplateId = value === undefined ? null : value;
    }
    public documentType: EnumDocumentType = EnumDocumentType.none;
    public dataTags: DataTag[] = [];
    /**
    * Will insert copies of this document into the final document
    */
    public numberOfExtraCopies: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.documentTemplateId = jsonSerializedObject.documentTemplateId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.documentTemplateId);
          this.documentType = EnumDocumentType.getByValue(jsonSerializedObject.documentType, this.documentType);
          this.dataTags = jsonSerializedObject.dataTags.map((v: any) => new DataTag(v));
          this.numberOfExtraCopies = jsonSerializedObject.numberOfExtraCopies;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.documentTemplateId = this.documentTemplateId == null ? null : this.documentTemplateId.toString();
        toRet.documentType = this.documentType.value;
        toRet.dataTags = this.dataTags.map(v => v.toJsonSerializedObject());
        toRet.numberOfExtraCopies = this.numberOfExtraCopies;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "documentTemplateId", rule: (v: any) => v == null || !v.isEmpty() || "DocumentPart.DocumentTemplateId cannot be empty"},
            {field: "dataTags", rule: (v: any) => !!v || "dataTags is required"},
            {field: "dataTags", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as DataTag[]).reduce((acc: Array<string>, curr: DataTag) => acc.concat(curr.validate()), new Array<string>());
            }},
            {field: "numberOfExtraCopies", rule: (v: any) => v == null || v >= -2147483648 || "NumberOfExtraCopies is not greater than minimum allowed value: -2147483648"},
            {field: "numberOfExtraCopies", rule: (v: any) => v == null || v <= 2147483647 || "NumberOfExtraCopies is above maximum allowed value: 2147483647"},
    ];
}


export class GridRowValue extends ModelObject<GridRowValue> {

    public cells: string[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.cells = jsonSerializedObject.cells.map((v: any) => v);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.cells = this.cells.map(v => v);
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class GridGroupValue extends ModelObject<GridGroupValue> {

    public groupName: string = "";
    public cells: string[] = [];
    public subGroups: GridGroupValue[] = [];
    public rows: GridRowValue[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.groupName = jsonSerializedObject.groupName;
          this.cells = jsonSerializedObject.cells.map((v: any) => v);
          this.subGroups = jsonSerializedObject.subGroups.map((v: any) => new GridGroupValue(v));
          this.rows = jsonSerializedObject.rows.map((v: any) => new GridRowValue(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.groupName = this.groupName;
        toRet.cells = this.cells.map(v => v);
        toRet.subGroups = this.subGroups.map(v => v.toJsonSerializedObject());
        toRet.rows = this.rows.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "subGroups", rule: (v: any) => !!v || "subGroups is required"},
            {field: "subGroups", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as GridGroupValue[]).reduce((acc: Array<string>, curr: GridGroupValue) => acc.concat(curr.validate()), new Array<string>());
            }},
            {field: "rows", rule: (v: any) => !!v || "rows is required"},
            {field: "rows", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as GridRowValue[]).reduce((acc: Array<string>, curr: GridRowValue) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class GridShape extends ModelObject<GridShape> {

    public title: string = "";
    public columns: string[] = [];
    public grid: GridGroupValue[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.title = jsonSerializedObject.title;
          this.columns = jsonSerializedObject.columns.map((v: any) => v);
          this.grid = jsonSerializedObject.grid.map((v: any) => new GridGroupValue(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.title = this.title;
        toRet.columns = this.columns.map(v => v);
        toRet.grid = this.grid.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "grid", rule: (v: any) => !!v || "grid is required"},
            {field: "grid", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as GridGroupValue[]).reduce((acc: Array<string>, curr: GridGroupValue) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class DocumentGenerationService extends ServiceBase {

  public async generateDocument(tenantId: Guid, documentTemplateId: Guid | null, documentType: EnumDocumentType, dataTags: readonly DataTag[], documentFormat: EnumDocumentFileType, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Uint8Array> {
    const req = {
    tenantId:tenantId.toString(),
    documentTemplateId:documentTemplateId == null ? null : documentTemplateId.toString(),
    documentType:documentType.value,
    dataTags:dataTags.map(v => v.toJsonSerializedObject()),
    documentFormat:documentFormat.value,
    };
    const extractor = (response:any) => converters.base64ToUint8Array(response.fileBytes);;
    return this.makeJsonRequest("documentGeneration/v1/generateDocument", req, false, extractor, opts);
  };

  public async generateDocuments(tenantId: Guid, documentFormat: EnumDocumentFileType, documentParts: readonly DocumentPart[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Uint8Array> {
    const req = {
    tenantId:tenantId.toString(),
    documentFormat:documentFormat.value,
    documentParts:documentParts.map(v => v.toJsonSerializedObject()),
    };
    const extractor = (response:any) => converters.base64ToUint8Array(response.fileBytes);;
    return this.makeJsonRequest("documentGeneration/v1/generateDocuments", req, false, extractor, opts);
  };

  public async generateGridDocument(tenantId: Guid, documentFormat: EnumDocumentFileType, title: string, columns: readonly string[], grid: readonly GridGroupValue[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Uint8Array> {
    const req = {
    tenantId:tenantId.toString(),
    documentFormat:documentFormat.value,
    title:title,
    columns:columns.map(v => v),
    grid:grid.map(v => v.toJsonSerializedObject()),
    };
    const extractor = (response:any) => converters.base64ToUint8Array(response.fileBytes);;
    return this.makeJsonRequest("documentGeneration/v1/generateGridDocument", req, false, extractor, opts);
  };

  public async getAllDataTagsByDocumentType(tenantId: Guid, documentType: EnumDocumentType, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<string[]> {
    const req = {
    tenantId:tenantId.toString(),
    documentType:documentType.value,
    };
    const extractor = (response:any) => response.dataTags.map((v: any) => v);;
    return this.makeJsonRequest("documentGeneration/v1/getAllDataTagsByDocumentType", req, false, extractor, opts);
  };

  public async generateMultipleGridsDocument(tenantId: Guid, documentFormat: EnumDocumentFileType, gridShapes: readonly GridShape[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<Uint8Array> {
    const req = {
    tenantId:tenantId.toString(),
    documentFormat:documentFormat.value,
    gridShapes:gridShapes.map(v => v.toJsonSerializedObject()),
    };
    const extractor = (response:any) => converters.base64ToUint8Array(response.fileBytes);;
    return this.makeJsonRequest("documentGeneration/v1/generateMultipleGridsDocument", req, false, extractor, opts);
  };

  public async generateGoogleSheetFile(tenantId: Guid, title: string, columns: readonly string[], grid: readonly GridGroupValue[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<string> {
    const req = {
    tenantId:tenantId.toString(),
    title:title,
    columns:columns.map(v => v),
    grid:grid.map(v => v.toJsonSerializedObject()),
    };
    const extractor = (response:any) => response.url;;
    return this.makeJsonRequest("documentGeneration/v1/generateGoogleSheetFile", req, false, extractor, opts);
  };
}

export const documentGenerationService = new DocumentGenerationService();